// =============================================================================
// Origin UI — src/pages/creditcards/CreditCardAdminPage.tsx
// Admin page for credit card statement period management.
// CC-10.1 through CC-10.9 (+ filters + bank column)
// Rabobank: CSV statements, one per cardholder — multi-file upload processed sequentially
// Card register, approvers and delegates live here as tabs (moved from Admin Configuration)
// so Credit Card Admins can self-serve without SysAdmin involvement.
// =============================================================================

import { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import { PageHeader, Card, ErrorBanner, LoadingSpinner, ConfirmDialog } from '@/components/common';
import {
  getPeriods, createPeriod, getPeriodStatus, uploadStatement, closePeriod, launchPeriod,
  getCards, getUnmatchedTransactions, bulkAssignUnmatched, getPersonalExpenses, deleteUpload, getReportTransactions,
  type ReportTransaction, type StatementPeriodListItem, type PeriodStatus,
  type CorporateCardListItem, type UnmatchedGroup, type PersonalExpenseSummary
} from '@/services/creditCardApi';
import { CorporateCardAdminTab } from '@/pages/admin/CorporateCardAdminTab';
import { CardApproverAdminTab } from '@/pages/admin/CardApproverAdminTab';
import { CreditCardDelegateAdminTab } from '@/pages/admin/CreditCardDelegateAdminTab';
import styles from './creditcards.module.css';

type AdminSection = 'periods' | 'cards' | 'approvers' | 'delegates';

const SECTIONS: { key: AdminSection; label: string }[] = [
  { key: 'periods', label: 'Statement Periods' },
  { key: 'cards', label: 'Cards' },
  { key: 'approvers', label: 'Approvers' },
  { key: 'delegates', label: 'Delegates' },
];

const ISSUER_CURRENCY: Record<string, string> = { ENBD: 'AED', DBS: 'SGD', JPM: 'USD', RABO: 'EUR' };

function parseApiError(e: any): string {
  try {
    const body = e?.body ? JSON.parse(e.body) : null;
    return body?.message || body?.error || e?.message || 'Upload failed.';
  } catch {
    return e?.message || 'Upload failed.';
  }
}

const STATUS_COLOURS: Record<string, string> = {
  Open: '#FF8300',
  UnderReview: '#00B0B9',
  Closed: '#22C55E',
  PendingReview: '#FF8300',
  Submitted: '#00B0B9',
  Approved: '#22C55E',
  Returned: '#EF4444',
};

export function CreditCardAdminPage() {
  const [section, setSection] = useState<AdminSection>('periods');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [periods, setPeriods] = useState<StatementPeriodListItem[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [periodDetail, setPeriodDetail] = useState<PeriodStatus | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [createYear, setCreateYear] = useState(new Date().getFullYear());
  const [createMonth, setCreateMonth] = useState(new Date().getMonth() + 1);
  const [creating, setCreating] = useState(false);

  const [uploading, setUploading] = useState<string | null>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showLaunchConfirm, setShowLaunchConfirm] = useState(false);
  const [launching, setLaunching] = useState(false);

  // Unmatched
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [unmatchedGroups, setUnmatchedGroups] = useState<UnmatchedGroup[]>([]);
  const [loadingUnmatched, setLoadingUnmatched] = useState(false);
  const [registeredCards, setRegisteredCards] = useState<CorporateCardListItem[]>([]);
  const [assigningGroup, setAssigningGroup] = useState<string | null>(null);
  const [selectedCardMap, setSelectedCardMap] = useState<Record<string, string>>({});

  // Personal
  const [showPersonal, setShowPersonal] = useState(false);
  const [personalData, setPersonalData] = useState<PersonalExpenseSummary | null>(null);
  const [loadingPersonal, setLoadingPersonal] = useState(false);

  // Review table filters
  const [filterCardholder, setFilterCardholder] = useState('');
  const [filterApprover, setFilterApprover] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBank, setFilterBank] = useState('');
  const [showReviewFilters, setShowReviewFilters] = useState(false);

  // Review table sort
  type ReviewSortKey = 'cardholderName' | 'cardIssuer' | 'approverName' | 'status' | 'totalAmount';
  const [reviewSortKey, setReviewSortKey] = useState<ReviewSortKey>('cardholderName');
  const [reviewSortDir, setReviewSortDir] = useState<'asc' | 'desc'>('asc');
  function toggleReviewSort(key: ReviewSortKey) {
    if (reviewSortKey === key) setReviewSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setReviewSortKey(key); setReviewSortDir('asc'); }
  }
  function reviewSortIcon(key: ReviewSortKey) { return reviewSortKey !== key ? ' \u21D5' : reviewSortDir === 'asc' ? ' \u2191' : ' \u2193'; }

  const [successMsg, setSuccessMsg] = useState('');
  const [reviewsCollapsed, setReviewsCollapsed] = useState(false);
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [expandedTxns, setExpandedTxns] = useState<ReportTransaction[]>([]);
  const [loadingTxns, setLoadingTxns] = useState(false);
  const [uploadToDelete, setUploadToDelete] = useState<{ statementUploadId: string; cardIssuer: string; fileName: string } | null>(null);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      const p = await getPeriods();
      setPeriods(p);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function selectPeriod(id: string) {
    setSelectedPeriodId(id);
    setShowUnmatched(false);
    setShowPersonal(false);
    clearReviewFilters();
    try {
      setLoadingDetail(true);
      setPeriodDetail(await getPeriodStatus(id));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingDetail(false);
    }
  }

  async function handleCreatePeriod() {
    try {
      setCreating(true);
      setError('');
      const p = await createPeriod({ periodYear: createYear, periodMonth: createMonth });
      setShowCreate(false);
      await load();
      selectPeriod(p.statementPeriodId);
    } catch (e: any) {
      const body = e.body ? JSON.parse(e.body) : null;
      setError(body?.error || e.message);
    } finally {
      setCreating(false);
    }
  }

  async function handleUploadFiles(files: File[]) {
    if (!selectedPeriodId || files.length === 0) return;
    setError('');
    setSuccessMsg('');
    const successLines: string[] = [];
    const errorLines: string[] = [];

    // Sequential — keeps the server-side replace/duplicate checks reliable
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploading(files.length > 1 ? `Uploading ${i + 1} of ${files.length}...` : 'Analysing...');
      try {
        const result = await uploadStatement(selectedPeriodId, 'AUTO', file);
        let line = `${result.cardIssuer} \u2014 ${file.name}: ${result.totalTransactions} transactions (${result.matchedTransactions} matched, ${result.unmatchedTransactions} unmatched)`;
        if (result.warnings && result.warnings.length > 0) line += ` \u2014 ${result.warnings.join(' ')}`;
        successLines.push(line);
      } catch (e: any) {
        errorLines.push(`${file.name}: ${parseApiError(e)}`);
      }
    }

    setUploading(null);
    if (successLines.length > 0) {
      setSuccessMsg(successLines.join('\n'));
      setTimeout(() => setSuccessMsg(''), files.length > 1 ? 15000 : 6000);
    }
    if (errorLines.length > 0) setError(errorLines.join(' | '));
    try { setPeriodDetail(await getPeriodStatus(selectedPeriodId)); } catch { /* ignore refresh error */ }
  }

  async function handleClosePeriod() {
    if (!selectedPeriodId) return;
    try {
      await closePeriod(selectedPeriodId);
      setShowCloseConfirm(false);
      setSuccessMsg('Period closed.');
      setTimeout(() => setSuccessMsg(''), 3000);
      await load();
      setPeriodDetail(await getPeriodStatus(selectedPeriodId));
    } catch (e: any) {
      const body = e.body ? JSON.parse(e.body) : null;
      setError(body?.error || e.message);
    }
  }

  async function handleLaunchPeriod() {
    try {
      setLaunching(true);
      await launchPeriod(selectedPeriodId!);
      setShowLaunchConfirm(false);
      setSuccessMsg('Period launched. Cardholders have been notified.');
      setTimeout(() => setSuccessMsg(''), 5000);
      await load();
      setPeriodDetail(await getPeriodStatus(selectedPeriodId!));
    } catch (e: any) {
      const body = e.body ? JSON.parse(e.body) : null;
      setError(body?.message || body?.error || e.message);
    } finally {
      setLaunching(false);
    }
  }

  async function toggleCardTransactions(corporateCardId: string) {
    if (expandedCardId === corporateCardId) {
      setExpandedCardId(null);
      setExpandedTxns([]);
      return;
    }
    if (!selectedPeriodId) return;
    setExpandedCardId(corporateCardId);
    setLoadingTxns(true);
    try {
      const data = await getReportTransactions(selectedPeriodId);
      const filtered = data.transactions.filter((t: any) => t.corporateCardId === corporateCardId || 
        (data.reviews.find((r: any) => r.corporateCardId === corporateCardId)?.cardholderName && 
         t.cardholderName === data.reviews.find((r: any) => r.corporateCardId === corporateCardId)?.cardholderName));
      setExpandedTxns(filtered);
    } catch { setExpandedTxns([]); }
    finally { setLoadingTxns(false); }
  }

  async function handleDeleteUpload() {
    if (!uploadToDelete || !selectedPeriodId) return;
    try {
      await deleteUpload(uploadToDelete.statementUploadId);
      setUploadToDelete(null);
      setSuccessMsg(uploadToDelete.cardIssuer + ' upload deleted.');
      setTimeout(() => setSuccessMsg(''), 3000);
      setPeriodDetail(await getPeriodStatus(selectedPeriodId));
    } catch (e: any) { setError(e.message); setUploadToDelete(null); }
  }

  // Unmatched
  async function loadUnmatched() {
    if (!selectedPeriodId) return;
    try {
      setLoadingUnmatched(true);
      setShowUnmatched(true);
      const [groups, cards] = await Promise.all([getUnmatchedTransactions(selectedPeriodId), getCards()]);
      setUnmatchedGroups(groups);
      setRegisteredCards(cards);
      setSelectedCardMap({});
    } catch (e: any) { setError(e.message); }
    finally { setLoadingUnmatched(false); }
  }

  async function handleBulkAssign(group: UnmatchedGroup) {
    if (!selectedPeriodId) return;
    const key = `${group.cardholderName}|${group.lastFourDigits}`;
    const cardId = selectedCardMap[key];
    if (!cardId) { setError('Select a card to assign to.'); return; }
    try {
      setAssigningGroup(key);
      await bulkAssignUnmatched(selectedPeriodId, { corporateCardId: cardId, cardholderNameOnStatement: group.cardholderName, lastFourDigits: group.lastFourDigits });
      setUnmatchedGroups(prev => prev.filter(g => !(g.cardholderName === group.cardholderName && g.lastFourDigits === group.lastFourDigits)));
      setPeriodDetail(await getPeriodStatus(selectedPeriodId));
      setSuccessMsg(`${group.transactionCount} transaction(s) for ${group.cardholderName} assigned.`);
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (e: any) { setError(e.message); }
    finally { setAssigningGroup(null); }
  }

  // Personal
  async function loadPersonalExpenses() {
    if (!selectedPeriodId) return;
    try {
      setLoadingPersonal(true);
      setShowPersonal(true);
      setPersonalData(await getPersonalExpenses(selectedPeriodId));
    } catch (e: any) { setError(e.message); }
    finally { setLoadingPersonal(false); }
  }

  // Review filters
  const cardholderOptions = useMemo(() => {
    if (!periodDetail) return [];
    return [...new Set(periodDetail.reviews.map(r => r.cardholderName))].sort();
  }, [periodDetail]);

  const approverOptions = useMemo(() => {
    if (!periodDetail) return [];
    return [...new Set(periodDetail.reviews.map(r => r.approverName))].sort();
  }, [periodDetail]);

  const bankOptions = useMemo(() => {
    if (!periodDetail) return [];
    return [...new Set(periodDetail.reviews.map(r => r.cardIssuer))].sort();
  }, [periodDetail]);

  const filteredReviews = useMemo(() => {
    if (!periodDetail) return [];
    let reviews = periodDetail.reviews;
    if (filterCardholder) reviews = reviews.filter(r => r.cardholderName === filterCardholder);
    if (filterApprover) reviews = reviews.filter(r => r.approverName === filterApprover);
    if (filterStatus) reviews = reviews.filter(r => r.status === filterStatus);
    if (filterBank) reviews = reviews.filter(r => r.cardIssuer === filterBank);
    return [...reviews].sort((a, b) => {
      let cmp = 0;
      if (reviewSortKey === 'totalAmount') cmp = a.totalAmount - b.totalAmount;
      else cmp = ((a as any)[reviewSortKey] || '').localeCompare((b as any)[reviewSortKey] || '');
      return reviewSortDir === 'asc' ? cmp : -cmp;
    });
  }, [periodDetail, filterCardholder, filterApprover, filterStatus, filterBank, reviewSortKey, reviewSortDir]);

  function clearReviewFilters() {
    setFilterCardholder(''); setFilterApprover(''); setFilterStatus(''); setFilterBank('');
  }

  const hasReviewFilters = filterCardholder || filterApprover || filterStatus || filterBank;

  if (loading) return <LoadingSpinner />;

  const currentPeriod = periods.find(p => p.statementPeriodId === selectedPeriodId);
  const hasReviews = periodDetail && periodDetail.reviews.length > 0;

  return (
    <div>
      <PageHeader title="Credit Card Administration" subtitle="Manage statement periods, uploads, cards, approvers and delegates" />

      {/* Section tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--gray-200)', marginBottom: 20 }}>
        {SECTIONS.map(s => (
          <button
            key={s.key}
            onClick={() => setSection(s.key)}
            style={{
              padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: section === s.key ? 700 : 500,
              color: section === s.key ? 'var(--turquoise-dark)' : 'var(--gray-500)',
              borderBottom: section === s.key ? '2px solid var(--turquoise)' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === 'cards' && <CorporateCardAdminTab />}
      {section === 'approvers' && <CardApproverAdminTab />}
      {section === 'delegates' && <CreditCardDelegateAdminTab />}

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {successMsg && (
        <div style={{ padding: '10px 16px', marginBottom: 16, borderRadius: 6, background: 'var(--green-light)', color: 'var(--green)', fontSize: 13, fontWeight: 600, whiteSpace: 'pre-line' }}>
          {successMsg}
        </div>
      )}

      {section === 'periods' && (
      <div className={styles.adminGrid}>
        {/* Left: Period list */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, color: 'var(--navy)', fontSize: 15 }}>Statement Periods</h3>
            <button className={styles.addBtn} onClick={() => setShowCreate(true)}>+ New Period</button>
          </div>

          {showCreate && (
            <div style={{ marginBottom: 12 }}>
            <Card>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div>
                  <label className={styles.fieldLabel}>Year</label>
                  <input type="number" className={styles.fieldInput} value={createYear} onChange={e => setCreateYear(Number(e.target.value))} style={{ width: 100 }} />
                </div>
                <div>
                  <label className={styles.fieldLabel}>Month</label>
                  <select className={styles.fieldInput} value={createMonth} onChange={e => setCreateMonth(Number(e.target.value))}>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>{new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}</option>
                    ))}
                  </select>
                </div>
                <button className={styles.saveBtn} onClick={handleCreatePeriod} disabled={creating}>{creating ? 'Creating...' : 'Create'}</button>
                <button className={styles.cancelBtn} onClick={() => setShowCreate(false)}>Cancel</button>
              </div>
            </Card>
            </div>
          )}

          {periods.map(p => (
            <div key={p.statementPeriodId} className={`${styles.periodCard} ${p.statementPeriodId === selectedPeriodId ? styles.periodCardActive : ''}`} onClick={() => selectPeriod(p.statementPeriodId)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, color: 'var(--navy)' }}>{p.periodName}</span>
                <span className={styles.statusBadge} style={{ background: `${STATUS_COLOURS[p.status] || '#666'}15`, color: STATUS_COLOURS[p.status] || '#666' }}>{p.status}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>{p.approvedCount}/{p.totalCards} approved</div>
            </div>
          ))}
          {periods.length === 0 && <p style={{ color: 'var(--gray-500)', fontSize: 13, textAlign: 'center', padding: 16 }}>No periods created yet.</p>}
        </div>

        {/* Right: Period detail */}
        <div>
          {!selectedPeriodId ? (
            <Card><p style={{ padding: 20, textAlign: 'center', color: 'var(--gray-500)' }}>Select a period to view details.</p></Card>
          ) : loadingDetail ? (
            <LoadingSpinner />
          ) : periodDetail ? (
            <>
              {/* Period action bar — Launch / Close at the top for quick access */}
              {currentPeriod?.status !== 'Closed' && (
                <div style={{ marginBottom: 16 }}>
                <Card>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 16 }}>{currentPeriod?.periodName}</div>
                      <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
                        {currentPeriod?.status === 'Open'
                          ? (hasReviews
                              ? 'Not yet launched \u2014 cardholders cannot see this period until you launch it.'
                              : 'Upload statements to get started.')
                          : `Under review \u2014 ${periodDetail.reviews.filter(r => r.status === 'Approved').length}/${periodDetail.reviews.length} approved.`}
                        {currentPeriod?.status === 'Open' && periodDetail.unmatchedTransactionCount > 0 && (
                          <span style={{ color: 'var(--orange)', fontWeight: 600 }}> {periodDetail.unmatchedTransactionCount} unmatched transaction(s) \u2014 resolve these before launching.</span>
                        )}
                      </div>
                    </div>

                    {currentPeriod?.status === 'Open' && hasReviews && (
                      <button style={{ padding: '10px 22px', borderRadius: 6, border: 'none', background: 'var(--orange)', color: 'white', fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => setShowLaunchConfirm(true)}>
                        Launch Period
                      </button>
                    )}

                    {currentPeriod?.status !== 'Open' && (
                      <button className={styles.closeBtn} onClick={() => setShowCloseConfirm(true)} disabled={periodDetail.reviews.some(r => r.status !== 'Approved') || periodDetail.reviews.length === 0}>
                        Close Period ({periodDetail.reviews.filter(r => r.status === 'Approved').length}/{periodDetail.reviews.length} approved)
                      </button>
                    )}
                  </div>
                </Card>
                </div>
              )}

              {/* Upload section */}
              {currentPeriod?.status !== 'Closed' && (
                <div style={{ marginBottom: 16 }}>
                <Card>
                  <h4 style={{ margin: '0 0 12px', color: 'var(--navy)', fontSize: 14 }}>Upload Statements</h4>
                  <UploadZone
                    issuer="Smart Upload"
                    existing={undefined}
                    uploading={uploading !== null}
                    uploadingLabel={uploading ?? undefined}
                    onUpload={(files) => handleUploadFiles(files)}
                    isSmartUpload={true}
                  />
                  {periodDetail.uploads.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Upload History</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {[...periodDetail.uploads].sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()).map(u => (
                          <div key={u.statementUploadId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--green-light)', borderRadius: 6, border: '1px solid var(--green)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12 }}>
                              <span style={{ fontWeight: 700, color: 'var(--green)', minWidth: 40 }}>{u.cardIssuer}</span>
                              <span style={{ color: 'var(--gray-600)' }}>{u.fileName}</span>
                              <span style={{ color: 'var(--gray-400)' }}>{u.transactionCount} transactions</span>
                              <span style={{ color: 'var(--gray-400)' }}>{new Date(u.uploadedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                            <button onClick={() => setUploadToDelete({ statementUploadId: u.statementUploadId, cardIssuer: u.cardIssuer, fileName: u.fileName })} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14, padding: '2px 6px' }} title="Delete upload">{String.fromCharCode(10005)}</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
                </div>
              )}

              {/* Review filters */}
              {hasReviews && (
                <div style={{ marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
                  {hasReviewFilters && <button onClick={clearReviewFilters} style={{ fontSize: 11, color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Clear</button>}
                  <button onClick={() => setShowReviewFilters(!showReviewFilters)} style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 4, cursor: 'pointer', background: showReviewFilters ? 'var(--turquoise-light)' : 'var(--gray-100)', color: showReviewFilters ? 'var(--turquoise-dark)' : 'var(--gray-600)', border: '1px solid ' + (showReviewFilters ? 'var(--turquoise)' : 'var(--gray-200)') }}>
                    Filters
                  </button>
                  {hasReviews && (
                    <button onClick={loadPersonalExpenses} style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 4, cursor: 'pointer', background: showPersonal ? 'var(--purple-light)' : 'transparent', color: 'var(--purple)', border: '1px solid var(--purple)' }}>
                      Personal Expenses
                    </button>
                  )}
                </div>
              )}

              {showReviewFilters && hasReviews && (
                <div style={{ marginBottom: 12 }}>
                <Card>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                    <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Cardholder</label>
                      <select className={styles.fieldInput} style={{ fontSize: 12 }} value={filterCardholder} onChange={e => setFilterCardholder(e.target.value)}><option value="">All</option>{cardholderOptions.map(n => <option key={n} value={n}>{n}</option>)}</select>
                    </div>
                    <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Approver</label>
                      <select className={styles.fieldInput} style={{ fontSize: 12 }} value={filterApprover} onChange={e => setFilterApprover(e.target.value)}><option value="">All</option>{approverOptions.map(n => <option key={n} value={n}>{n}</option>)}</select>
                    </div>
                    <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Bank</label>
                      <select className={styles.fieldInput} style={{ fontSize: 12 }} value={filterBank} onChange={e => setFilterBank(e.target.value)}><option value="">All</option>{bankOptions.map(n => <option key={n} value={n}>{n}</option>)}</select>
                    </div>
                    <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Status</label>
                      <select className={styles.fieldInput} style={{ fontSize: 12 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}><option value="">All</option><option value="PendingReview">Pending Review</option><option value="Submitted">Submitted</option><option value="Approved">Approved</option><option value="Returned">Returned</option></select>
                    </div>
                  </div>
                </Card>
                </div>
              )}

              {/* Status grid */}
              <div style={{ marginBottom: 16 }}>
                <Card padding={false}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4 style={{ margin: 0, color: 'var(--navy)', fontSize: 14, cursor: 'pointer' }} onClick={() => setReviewsCollapsed(c => !c)}>
                    {reviewsCollapsed ? '\u25B6' : '\u25BC'} Card Review Status
                    {hasReviewFilters && <span style={{ fontSize: 12, color: 'var(--turquoise)', marginLeft: 8 }}>({filteredReviews.length} of {periodDetail.reviews.length})</span>}
                  </h4>
                  {periodDetail.unmatchedTransactionCount > 0 && (
                    <span style={{ fontSize: 12, color: 'var(--orange)', fontWeight: 600 }}>{periodDetail.unmatchedTransactionCount} unmatched</span>
                  )}
                </div>
                {!reviewsCollapsed && <div className={styles.txTableWrap}>
                  <table className={styles.txTable}>
                    <thead>
                      <tr>
                        <th style={{ cursor: 'pointer' }} onClick={() => toggleReviewSort('cardholderName')}>Cardholder{reviewSortIcon('cardholderName')}</th>
                        <th>Card</th>
                        <th style={{ cursor: 'pointer' }} onClick={() => toggleReviewSort('cardIssuer')}>Bank{reviewSortIcon('cardIssuer')}</th>
                        <th style={{ cursor: 'pointer' }} onClick={() => toggleReviewSort('approverName')}>Approver{reviewSortIcon('approverName')}</th>
                        <th style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleReviewSort('status')}>Status{reviewSortIcon('status')}</th>
                        <th style={{ textAlign: 'center' }}>Reviewed</th>
                        <th style={{ textAlign: 'right' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredReviews.map(r => (
                        <Fragment key={r.cardStatementReviewId}>
                        <tr>
                          <td style={{ fontWeight: 600, cursor: 'pointer', color: 'var(--turquoise-dark)' }} onClick={() => toggleCardTransactions(r.corporateCardId)} title="Click to view transactions">{r.cardholderName} {expandedCardId === r.corporateCardId ? '\u25B2' : '\u25BC'}</td>
                          <td>•••• {r.cardLastFourDigits}</td>
                          <td>{r.cardIssuer}</td>
                          <td>{r.approverName}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={styles.statusBadge} style={{ background: `${STATUS_COLOURS[r.status] || '#666'}15`, color: STATUS_COLOURS[r.status] || '#666' }}>{r.status}</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>{r.reviewedTransactionCount}/{r.transactionCount}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{r.cardCurrency || ISSUER_CURRENCY[r.cardIssuer] || 'AED'} {r.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                        </tr>
                        {expandedCardId === r.corporateCardId && (
                          <tr>
                            <td colSpan={7} style={{ padding: 0, borderTop: 'none' }}>
                              <div style={{ padding: '12px 16px', background: 'var(--gray-50)', borderTop: '1px solid var(--gray-200)' }}>
                                {loadingTxns ? (
                                  <div style={{ padding: 12, textAlign: 'center', fontSize: 12, color: 'var(--gray-400)' }}>Loading transactions...</div>
                                ) : expandedTxns.length === 0 ? (
                                  <div style={{ padding: 12, textAlign: 'center', fontSize: 12, color: 'var(--gray-400)' }}>No transactions found.</div>
                                ) : (
                                  <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                                    <thead>
                                      <tr style={{ background: 'var(--gray-100)' }}>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Merchant</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Amount</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Commentary</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Flags</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {expandedTxns.map((t, i) => (
                                        <tr key={i} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                                          <td style={{ padding: '6px 8px', color: 'var(--gray-500)' }}>{new Date(t.transactionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</td>
                                          <td style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--navy)' }}>{t.merchantName}</td>
                                          <td style={{ padding: '6px 8px', color: 'var(--gray-500)' }}>{t.merchantCategory || '\u2014'}</td>
                                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{t.currency} {Number(t.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                          <td style={{ padding: '6px 8px', color: t.commentary ? 'var(--gray-600)' : 'var(--gray-300)' }}>{t.commentary || 'No commentary'}</td>
                                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                            {t.isFlaggedPersonal && <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 3, background: 'var(--purple-light)', color: 'var(--purple)', fontSize: 10, fontWeight: 600, marginRight: 4 }}>P</span>}
                                            {t.isFlaggedDisputed && <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 3, background: 'var(--red-light)', color: 'var(--red)', fontSize: 10, fontWeight: 600 }}>D</span>}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                        </Fragment>
                      ))}
                      {filteredReviews.length === 0 && (
                        <tr><td colSpan={7} style={{ textAlign: 'center', padding: 20, color: 'var(--gray-400)' }}>{hasReviewFilters ? 'No reviews match filters.' : 'No reviews yet \u2014 upload statements to begin.'}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>}
              </Card>
              </div>

              {/* Personal expenses */}
              {showPersonal && (
                <div style={{ marginBottom: 16 }}>
                <Card>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h4 style={{ margin: 0, color: 'var(--purple)', fontSize: 14 }}>{'\uD83C\uDFF7\uFE0F'} Personal Expense Report</h4>
                    <button onClick={() => setShowPersonal(false)} style={{ fontSize: 11, color: 'var(--gray-500)', background: 'none', border: 'none', cursor: 'pointer' }}>Close {'\u2715'}</button>
                  </div>
                  {loadingPersonal ? <LoadingSpinner /> : personalData ? (
                    personalData.totalCount === 0 ? (
                      <p style={{ fontSize: 13, color: 'var(--gray-500)', textAlign: 'center', padding: 12, margin: 0 }}>No personal expenses flagged for this period.</p>
                    ) : (
                      <>
                        <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                          <div style={{ padding: '8px 14px', background: 'var(--purple-light)', borderRadius: 6 }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--purple)' }}>{personalData.totalCount}</div>
                            <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>Transactions</div>
                          </div>
                          {personalData.byCurrency.map(c => (
                            <div key={c.currency} style={{ padding: '8px 14px', background: 'var(--purple-light)', borderRadius: 6 }}>
                              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--purple)' }}>{c.currency} {c.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                              <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{c.count} txn(s)</div>
                            </div>
                          ))}
                        </div>
                        <div className={styles.txTableWrap}>
                          <table className={styles.txTable}>
                            <thead><tr><th>Employee</th><th>Card</th><th>Bank</th><th style={{ width: 80 }}>Date</th><th>Merchant</th><th style={{ textAlign: 'right' }}>Amount</th><th>Commentary</th></tr></thead>
                            <tbody>
                              {personalData.transactions.map(t => (
                                <tr key={t.cardTransactionId}>
                                  <td style={{ fontWeight: 600 }}>{t.employeeName}{t.employeeNumber && <span style={{ fontSize: 11, color: 'var(--gray-400)', marginLeft: 4 }}>({t.employeeNumber})</span>}</td>
                                  <td>{'\u2022\u2022\u2022\u2022'} {t.cardLastFourDigits}</td>
                                  <td>{t.cardIssuer}</td>
                                  <td>{new Date(t.transactionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</td>
                                  <td>{t.merchantName}</td>
                                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{t.currency} {t.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                  <td style={{ fontSize: 12, color: 'var(--gray-600)' }}>{t.commentary || '\u2014'}{t.isFlaggedDisputed && <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 3, background: 'var(--red-light)', color: '#C33A30', fontSize: 10, fontWeight: 600, marginLeft: 4 }}>Disputed</span>}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )
                  ) : null}
                </Card>
                </div>
              )}

              {/* Unmatched */}
              {periodDetail.unmatchedTransactionCount > 0 && (
                <div style={{ marginBottom: 16 }}>
                <Card>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h4 style={{ margin: 0, color: 'var(--orange)', fontSize: 14 }}>{'\u26A0\uFE0F'} Unmatched Transactions ({periodDetail.unmatchedTransactionCount})</h4>
                    <button onClick={loadUnmatched} style={{ fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', background: showUnmatched ? 'var(--gray-100)' : 'var(--orange)', color: showUnmatched ? 'var(--gray-600)' : '#fff', border: showUnmatched ? '1px solid var(--gray-300)' : 'none' }}>
                      {showUnmatched ? 'Refresh' : 'Resolve'}
                    </button>
                  </div>
                  {!showUnmatched && <p style={{ fontSize: 13, color: 'var(--gray-600)', margin: 0 }}>Click "Resolve" to assign unmatched transactions to registered cards.</p>}
                  {showUnmatched && loadingUnmatched && <LoadingSpinner />}
                  {showUnmatched && !loadingUnmatched && unmatchedGroups.length === 0 && <p style={{ fontSize: 13, color: 'var(--green)', textAlign: 'center', padding: 12, margin: 0 }}>All transactions are now matched.</p>}
                  {showUnmatched && !loadingUnmatched && unmatchedGroups.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
                      {unmatchedGroups.map(group => {
                        const key = `${group.cardholderName}|${group.lastFourDigits}`;
                        const isAssigning = assigningGroup === key;
                        return (
                          <div key={key} style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: '12px 16px', background: 'var(--gray-50)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                              <div style={{ flex: 1, minWidth: 200 }}>
                                <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 14 }}>{group.cardholderName}</div>
                                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>{'\u2022\u2022\u2022\u2022'} {group.lastFourDigits} {'\u00B7'} {group.transactionCount} txn(s) {'\u00B7'} {group.currency} {group.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                              </div>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <select className={styles.fieldInput} style={{ width: 220, fontSize: 12, padding: '6px 10px' }} value={selectedCardMap[key] || ''} onChange={e => setSelectedCardMap(prev => ({ ...prev, [key]: e.target.value }))}>
                                  <option value="">Select a card...</option>
                                  {[...registeredCards].sort((a, b) => (a.employeeName || '').localeCompare(b.employeeName || '')).map(c => <option key={c.corporateCardId} value={c.corporateCardId}>{c.employeeName} {'\u2014'} {'\u2022\u2022\u2022\u2022'} {c.lastFourDigits} ({c.cardIssuer})</option>)}
                                </select>
                                <button onClick={() => handleBulkAssign(group)} disabled={!selectedCardMap[key] || isAssigning} style={{ fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 6, background: selectedCardMap[key] ? 'var(--turquoise)' : 'var(--gray-200)', color: selectedCardMap[key] ? '#fff' : 'var(--gray-400)', border: 'none', cursor: selectedCardMap[key] ? 'pointer' : 'default', whiteSpace: 'nowrap' }}>
                                  {isAssigning ? 'Assigning...' : 'Assign All'}
                                </button>
                              </div>
                            </div>
                            <details style={{ marginTop: 8 }}>
                              <summary style={{ fontSize: 11, color: 'var(--turquoise-dark)', cursor: 'pointer', fontWeight: 600 }}>View transactions</summary>
                              <div style={{ marginTop: 6 }}>
                                {group.transactions.map(t => (
                                  <div key={t.cardTransactionId} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--gray-100)', fontSize: 12, color: 'var(--gray-600)' }}>
                                    <span>{new Date(t.transactionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                                    <span style={{ flex: 1, marginLeft: 12 }}>{t.merchantName}</span>
                                    <span style={{ fontWeight: 600, color: 'var(--navy)' }}>{t.currency} {t.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                  </div>
                                ))}
                              </div>
                            </details>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
                </div>
              )}

            </>
          ) : null}
        </div>
      </div>
      )}

      <ConfirmDialog open={showLaunchConfirm} title="Launch Statement Period" message={`Launch ${currentPeriod?.periodName}? Cardholders will be able to see their transactions and will be emailed that they are ready for review.`} confirmLabel={launching ? "Launching..." : "Launch"} onConfirm={handleLaunchPeriod} onCancel={() => setShowLaunchConfirm(false)} />
      <ConfirmDialog open={showCloseConfirm} title="Close Statement Period" message={`Are you sure you want to close ${currentPeriod?.periodName}? This action cannot be undone.`} confirmLabel="Close Period" onConfirm={handleClosePeriod} onCancel={() => setShowCloseConfirm(false)} />

      <ConfirmDialog
        open={!!uploadToDelete}
        title="Delete Upload"
        message={uploadToDelete ? `Delete the ${uploadToDelete.cardIssuer} upload ("${uploadToDelete.fileName}")? All transactions from this upload will be removed.` : ''}
        confirmLabel="Delete"
        onConfirm={handleDeleteUpload}
        onCancel={() => setUploadToDelete(null)}
      />
    </div>
  );
}

function UploadZone({ issuer, existing, uploading, uploadingLabel, onUpload, isSmartUpload }: { issuer: string; existing?: { fileName: string; transactionCount: number; uploadedAt: string; uploadedByName: string }; uploading: boolean; uploadingLabel?: string; onUpload: (files: File[]) => void; isSmartUpload?: boolean; }) {
  const fileRef = useRef<HTMLInputElement>(null);
  return (
    <div className={styles.uploadZone} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); if (!uploading && e.dataTransfer.files.length > 0) onUpload(Array.from(e.dataTransfer.files)); }} onClick={() => { if (!uploading) fileRef.current?.click(); }}>
      <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" multiple hidden onChange={e => { if (e.target.files && e.target.files.length > 0) onUpload(Array.from(e.target.files)); e.target.value = ''; }} />
      <div className={styles.uploadIssuer}>{isSmartUpload ? '🧠 Smart Upload' : issuer}</div>
      {uploading ? <div className={styles.uploadStatus}>{uploadingLabel || (isSmartUpload ? 'Analysing with AI...' : 'Uploading...')}</div> : existing ? (
        <div className={styles.uploadStatus}>
          <div style={{ fontWeight: 600, fontSize: 12 }}>{existing.fileName}</div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{existing.transactionCount} transactions {'\u2022'} {new Date(existing.uploadedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
          <div style={{ fontSize: 11, color: 'var(--turquoise)', marginTop: 4 }}>Click to re-upload</div>
        </div>
      ) : <div className={styles.uploadStatus} style={{ color: isSmartUpload ? 'var(--turquoise-dark)' : 'var(--gray-400)' }}>{isSmartUpload ? 'Drop one or more bank statements \u2014 AI detects the format. Rabobank: select all cardholder CSVs at once.' : 'Drop file or click to upload'}</div>}
    </div>
  );
}
